import { useEffect, useState, useRef } from 'react';
import "dropzone/dist/dropzone.css";
import { Dropzone } from "dropzone";
import { useOktaAuth } from '@okta/okta-react';
import OutlinedDiv from '../layout/OutlinedDiv';
import { useParams } from 'react-router';
import { TextField, Tooltip } from '@mui/material';
import { MdContentCopy } from "react-icons/md";
import SpinnerLoader from '../layout/spinner-loader';
import './legal-description-extractor.scss';

const LegalDescriptionExtractor = () => {
    const { oktaAuth, authState } = useOktaAuth();
    const dropzoneRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [rwLoggingStatus, setRwLoggingStatus] = useState(null);
    const [rwFileNumber, setRwFileNumber] = useState('');
    const [copied, setCopied] = useState(false);
    const [fileDropzone, setFileDropzone] = useState(null);
    const [legalDescription, setLegalDescription] = useState(null);
    let { paramRwFileNumber } = useParams();

    useEffect(() => {
        if (paramRwFileNumber && rwFileNumber === '') setRwFileNumber(paramRwFileNumber);
    }, [paramRwFileNumber]);

    useEffect(() => {
        if (!authState) return;

        if (fileDropzone) return;

        const dz = new Dropzone(dropzoneRef.current,
            {
                autoProcessQueue: false,
                url: process.env.REACT_APP_API_ADMIN_BASE_URL + "/document-intelligence/legal-descr-extraction",
                headers: { 'Authorization': `Bearer UpdateBeforeSending` },
                acceptedFiles: 'application/pdf,.doc,.docx',
                addRemoveLinks: true,
                maxFilesize: 15, maxFiles: 1,
                init: function() {                    
                    this.on("removedfile", function () {
                        resetForm();
                    });

                    this.on("sending", (data, xhr, formData) => {
                        setSubmitting(true);
                        formData.append('fileName', data.upload.filename);
                        formData.append('rwFileNumber', document.getElementById("rwFileNumber").value);
                    });

                    this.on("success", function (file, response) {
                        if (response === '') {
                            setError('Legal description could not be found in the file.');
                        }
                        setLegalDescription(response.legalDescription);
                        setRwLoggingStatus(response.status);
                        file.status = 'queued';
                        file.upload.bytesSent = 0;
                        file.upload.progress = 0;
                    });

                    this.on("error", function (file, message) {
                        setError(message);
                        file.status = 'queued';
                        file.upload.bytesSent = 0;
                        file.upload.progress = 0;
                    });

                    this.on("complete", function(x) {
                        setSubmitting(false);
                    });
                }
            });
        setFileDropzone(dz);
    }, [authState]);

    const resetForm = () => {
        setRwFileNumber('');
        setError(null);
        setLegalDescription(null);
        setRwLoggingStatus(null);
        if (fileDropzone && fileDropzone.getAcceptedFiles().length > 0) fileDropzone.removeFile(fileDropzone.getAcceptedFiles()[0]);
    }

    const processFile = async (e) => {
        var errorMsg = '';
        if (fileDropzone.getAcceptedFiles().length === 0) {
            errorMsg = 'Please add a deed that has a legal description in it.';
        }
        setError(errorMsg);
        if (errorMsg !== '') return;

        const accessTokenInfoRefresh = await oktaAuth.tokenManager.get('accessToken');
        fileDropzone.options.headers = { 'Authorization': `Bearer ${accessTokenInfoRefresh.accessToken}` };

        e.preventDefault();
        e.stopPropagation();
        fileDropzone.processQueue();
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(legalDescription);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    return (
        <div className="legal-description-extractor">
            <title>Legal Description Extractor</title>
            <h1>Legal Description Extractor</h1>

            <div className="mb-3">
                <div className="mb-3">Add a note with the legal description to a ResWare File? <span className="text-small">(optional)</span></div>
                <TextField id="rwFileNumber"
                    value={rwFileNumber}
                    label="File Number"
                    className="w-100"
                    onChange={(e) => setRwFileNumber(e.target.value)}
                /> 
            </div>
            <div className="mb-3">
                <OutlinedDiv label="File Upload">
                    <div id="deedFileDropzone" className="dropzone dropzone-previews border-0" ref={dropzoneRef} >
                        <div className="dz-message" data-dz-message><span>Click to browse, or drag and drop a deed here to have the Legal Description extracted.</span></div>
                    </div>
                </OutlinedDiv>
            </div>
            <div className="py-3">
                <input type="button" className="btn btn-primary" disabled={submitting} value="Extract Legal Description" onClick={processFile} /> {!submitting && (legalDescription || error) && <input type="button" className="btn btn-primary" disabled={submitting} value="Reset" onClick={resetForm} />}
                {rwLoggingStatus && <span className="text-success ms-2">{rwLoggingStatus}</span>}
            </div>
            {error && <div className="text-danger">{error}</div> }

            {submitting && <SpinnerLoader />}
            
            {!submitting && legalDescription &&
                <div className="d-flex justfy-content-center">
                    <div className="copy-box p-1 lh-sm me-1">
                            {legalDescription}
                    </div>
                    <div className="align-self-start ms-1">
                        <Tooltip title="Copy to clipboard">
                            <button type="button" className="btn btn-primary mx-2" onClick={handleCopy}> <MdContentCopy /></button>
                        </Tooltip>
                        {copied && <div className="text-sm">Copied!</div>}
                    </div>
                </div>}
        </div>            
    );
}

export default LegalDescriptionExtractor;
